/*
 * Default tiers copy shared-cs `WindRules.DefaultTiers` (colour slots PrintInk, Butter, FxRed).
 * WIND (ADR 0073, docs/architecture/wind-contract.md section 3): the C# initializers of shared-cs `WindDef` (`raidRules.wind`),
 * `WindTierDef` (`winds[]`) and `MedDef` (`items[].med`), plus the wind fields added to existing blocks (section 3.7).
 * A form shows these when an older draft doesn't have the field yet; nothing is written to the draft until a field is edited.
 * Clamps live with the fields (`WIND_LIMITS`), copied from the contract; the validators (lib/validate, stream W1b) own the rules.
 */
import type { Schemas } from '@argzombie/api-client';

export type WindDef = Schemas['WindDef'];
export type WindTierDef = Schemas['WindTierDef'];
export type MedDef = Schemas['MedDef'];

/** Every field present, nested blocks included (what the server has after parsing). */
export type DeepRequired<T> = T extends (infer U)[]
  ? U[]
  : T extends object
    ? { [K in keyof T]-?: DeepRequired<NonNullable<T[K]>> }
    : T;

/** `raidRules.wind` as the server reads it. */
export type WindParsed = DeepRequired<WindDef>;

/** shared-cs `WindDef` defaults (contract 3.1). */
export const windDefaults = (): WindParsed => ({
  capacity: 100,
  drop: { graceS: 0.5, landFrac: 0.5 },
  recovery: {
    pauseS: 1,
    pauseAfterDropS: 2.5,
    fastPerS: 8,
    fastUntilFrac: 0.7,
    slowPerS: 3,
    walkMult: 0.4,
    crouchMult: 0.6,
    holdUnderThreatMult: 0.4,
    threatGate: true,
  },
  sprint: { costPerS: 6, minToStart: 15 },
  costs: {
    pushOff: 12,
    takedown: 6,
    kick: 6,
    throw: 4,
    reload: 1,
    sustainedFireWindowS: 0.3,
  },
  bite: { baseOdds: 0.04, spentK: 1.5, maxOdds: 0.9 },
  hits: { damageToWind: 1 },
  wounds: { toDie: 3, toDown: 2, capacityMultByWounds: [1, 0.8, 0.5] },
  rest: {
    hoursPerWind: { home: 2, outpost: 3, away: 4 },
    debtPerWound: 0.5,
    maxDebt: 3,
    startFloorTier: 'gassed',
  },
  startTier: { v4ResumeTier: 'fresh' },
});

/** Kept for the brief's name: the same object as `windDefaults()`. */
export const WIND_DEFAULTS = windDefaults();

/** shared-cs default `winds[]` (contract 3.2): order = tier index. */
export const windTierDefaults = (): Required<WindTierDef>[] => [
  {
    id: 'fresh',
    name: 'FRESH',
    capacityMult: 1,
    recoveryMult: 1,
    biteOddsMult: 1,
    climbBackCalmS: 0,
    swingIntervalMult: 1,
    moveSpeedMult: 1,
    acquireMult: 1,
    spreadMult: 1,
    colorSlot: 'PrintInk',
  },
  {
    id: 'winded',
    name: 'WINDED',
    capacityMult: 0.6,
    recoveryMult: 0.6,
    biteOddsMult: 2,
    climbBackCalmS: 20,
    swingIntervalMult: 1.1,
    moveSpeedMult: 0.95,
    acquireMult: 1.1,
    spreadMult: 1.15,
    colorSlot: 'Butter',
  },
  {
    id: 'gassed',
    name: 'GASSED',
    capacityMult: 0.3,
    recoveryMult: 0.3,
    biteOddsMult: 4,
    climbBackCalmS: 30,
    swingIntervalMult: 1.2,
    moveSpeedMult: 0.9,
    acquireMult: 1.2,
    spreadMult: 1.3,
    colorSlot: 'FxRed',
  },
];

export const WIND_TIER_DEFAULTS = windTierDefaults();

/** The tier ids a tier select offers: the draft's `winds[]` ids, or the default tiers when the draft has none yet. */
export function windTierIds(
  winds: (WindTierDef | null | undefined)[] | null | undefined,
): string[] {
  const ids = (winds ?? []).map((w) => w?.id ?? '').filter((id) => id !== '');
  return ids.length > 0 ? ids : WIND_TIER_DEFAULTS.map((t) => t.id);
}

/** A new tier row for the Wind tiers page: the last default tier's numbers under the given id. */
export const newWindTier = (id: string): Required<WindTierDef> => ({
  ...(WIND_TIER_DEFAULTS[WIND_TIER_DEFAULTS.length - 1] as Required<WindTierDef>),
  id,
  name: id.toUpperCase(),
});

type Obj = Record<string, unknown>;
const isObj = (v: unknown): v is Obj => v !== null && typeof v === 'object' && !Array.isArray(v);

/** Deep merge for nested blocks (Newtonsoft fills absent nested fields from the initializers; lists are replaced whole). */
export function mergeDefaults<T extends object>(defaults: T, value: unknown): T {
  if (!isObj(value)) return defaults;
  const out: Obj = { ...(defaults as Obj) };
  for (const [k, v] of Object.entries(value)) {
    if (v === undefined) continue;
    const d = out[k];
    out[k] = isObj(d) && isObj(v) ? mergeDefaults(d, v) : v;
  }
  return out as T;
}

/** `raidRules.wind` as the server reads it: absent fields are the C# defaults. */
export const windAsParsed = (value: WindDef | null | undefined): WindParsed =>
  mergeDefaults(windDefaults(), value);

/** shared-cs `MedDef` defaults: a med that does nothing until its fields are set. */
export const medDefaults = (): Required<MedDef> => ({
  wind: 0,
  woundPenaltyClear: 0,
  climbTiers: 0,
  climbBarFrac: 0,
  recoveryMult: 1,
  recoveryS: 0,
  restWinds: 0,
});

/** The wind fields of `items[].weapon` (contract 3.3): `windCost` is required on every weapon (no default the validator accepts). */
export const WEAPON_WIND_DEFAULTS = { windCost: 0, windCostPerS: 0, recoveryMult: 1 } as const;

/**
 * The wind fields of a new `items[].armor` (contract 3.4). On an existing piece an absent `windGuard` (C# null) means "not set":
 * the tank falls back to the old `bonusHealth`.
 */
export const ARMOR_WIND_DEFAULTS = {
  windGuard: 0,
  windCostMult: 1,
  biteOddsMult: 1,
  blocksPerRaid: 1,
} as const;

/**
 * The wind fields of `zombies[]` (contract 3.5). `attack` is required in a wind bundle (grab | hit | mixed); C# null = not set
 * (an older bundle reads grab). `hitChance` only counts for mixed.
 */
export const ZOMBIE_ATTACKS = ['grab', 'hit', 'mixed'] as const;
export const ZOMBIE_WIND_DEFAULTS = {
  hitChance: 0,
  pushOffMult: 1,
  biteOddsMult: 1,
  grabTelegraphS: 0.5,
} as const;

/** `squadRules.ai` wind fields (contract 3.7). */
export const SQUAD_AI_WIND_DEFAULTS = {
  fallBackAtTier: 'winded',
  clutchAtTier: 'gassed',
  hurtWindowS: 10,
} as const;

/** `squadRules` wind fields (contract 3.7). */
export const SQUAD_WIND_DEFAULTS = {
  downedWoundsToDie: 1,
  reviveTier: 'winded',
  reviveWindFrac: 0.3,
} as const;

/** `conditions` wind fields (contract 3.7); `maxBiteChance` (0.6) is an existing field, in `conditionsDefaults`. */
export const CONDITIONS_WIND_DEFAULTS = {
  minWindCapacityMult: 0.5,
  woundedAtTier: 'gassed',
  woundedAtWounds: 1,
  laidUpAtTier: 'gassed',
  laidUpAtWounds: 1,
  biteChancePerGrab: 0.01,
  playerTurnedMaxTier: 'winded',
} as const;

/** `conditions.types[]` wind fields by condition id (contract 3.7); other ids take 1 and 1. */
export function conditionTypeWindDefaults(id: string | null | undefined) {
  return {
    windCapacityMult: id === 'wounded' ? 0.85 : id === 'bitten' ? 0.9 : 1,
    windRecoveryMult: id === 'sick' ? 0.8 : 1,
  };
}

/** `baseModules[infirmary].levels[].restMult`: the seed's 0.8 / 0.6 / 0.4 by level (C# default 0 = not set, reads 1). */
export const INFIRMARY_REST_MULTS = [0.8, 0.6, 0.4] as const;

/** `homeRules` wind fields (contract 3.7). */
export const HOME_WIND_DEFAULTS = { dischargeMarkMinRest: 1, badlyHurtTier: 'gassed' } as const;

/**
 * `healthRules` wind fields (contract 3.7): five numbers each, none / walker / mover / athlete / beast. C# empty lists read these
 * (`HealthLevelWindDef.DefaultRecovery` / `DefaultRest`).
 */
export const HEALTH_LEVEL_NAMES = ['none', 'walker', 'mover', 'athlete', 'beast'] as const;
export const healthWindDefaults = () => ({
  levelMinTier: 2,
  levelWind: { recovery: [1, 1.15, 1.3, 1.5, 1.7], rest: [1, 1.3, 1.6, 2, 2.5] },
});

/** `survival.starvationDebtPerDay` and `resurrection.kit.fullyRested` (contract 3.7). */
export const SURVIVAL_WIND_DEFAULTS = { starvationDebtPerDay: 1 } as const;
export const RESURRECTION_KIT_WIND_DEFAULTS = { fullyRested: true, restTier: 'winded' } as const;

/** The `grant_wind` trigger block (contract 3.7; shared-cs `TriggerWindBlock`): rest debt removed, 0.5..3. */
export const triggerWindDefaults = (): Required<Schemas['TriggerWindBlock']> => ({
  winds: 0,
  target: 'player',
});
